// Hybrid holdings discovery for Robinhood Chain (4663). Uniswap's portfolio backend doesn't index
// 4663, so we discover which token CONTRACTS a wallet holds via the chain's OWN explorer (Blockscout —
// never Uniswap), then read the authoritative BALANCES on-chain (see balances.ts). Everything from
// Blockscout is treated as UNTRUSTED display/discovery data; its reported balances are never used for
// anything, and only well-formed ERC-20 contract addresses are kept.

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
/** Hard cap on how many discovered tokens we process, so a huge/hostile response can't blow up the
 * balance batch or the UI. */
export const MAX_DISCOVERED_TOKENS = 200

export interface DiscoveredToken {
  /** Validated ERC-20 contract address (0x + 40 hex). */
  address: string
  /** Untrusted presentation metadata. */
  symbol?: string
  name?: string
  /** Blockscout-reported decimals — untrusted; the consumer prefers curated, else verifies on-chain. */
  decimals?: number
  logoURI?: string
  /** Untrusted USD price (display only). */
  priceUsd?: number
  reputation?: string
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return undefined
}

/**
 * Parses a Blockscout `/addresses/{addr}/token-balances` response into validated discovered tokens.
 * Pure + defensive: rejects non-EVM addresses, non-ERC-20 entries, and implausible decimals;
 * deduplicates by lowercase address; caps the count. Never surfaces the reported balance.
 */
export function parseBlockscoutHoldings(json: unknown): DiscoveredToken[] {
  const items = Array.isArray(json) ? json : Array.isArray((json as { items?: unknown[] })?.items) ? (json as { items: unknown[] }).items : []
  const seen = new Set<string>()
  const out: DiscoveredToken[] = []

  for (const raw of items) {
    if (out.length >= MAX_DISCOVERED_TOKENS) break
    const tok = (raw as { token?: Record<string, unknown> })?.token
    if (!tok || typeof tok !== 'object') continue
    // Only fungible ERC-20s (skip ERC-721/1155 and anything with a token_id).
    if (typeof tok.type === 'string' && tok.type !== 'ERC-20') continue
    if ((raw as { token_id?: unknown }).token_id != null) continue

    const address = (tok.address_hash ?? tok.address) as unknown
    if (typeof address !== 'string' || !ADDRESS_RE.test(address)) continue
    const key = address.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)

    const dec = asNumber(tok.decimals)
    const price = asNumber(tok.exchange_rate)
    // Blockscout's icon_url is deliberately NOT surfaced: it is attacker-controlled (a token
    // deployer sets it to any host), and rendering it in the tray would leak the viewer's IP +
    // wallet-open timing to that host. Discovered tokens render a local blank logo instead; only
    // curated tokens (fixed trusted set) get a real image. See useRobinhoodPortfolio.

    out.push({
      address,
      symbol: typeof tok.symbol === 'string' ? tok.symbol : undefined,
      name: typeof tok.name === 'string' ? tok.name : undefined,
      decimals: dec !== undefined && Number.isInteger(dec) && dec >= 0 && dec <= 36 ? dec : undefined,
      priceUsd: price !== undefined && price >= 0 ? price : undefined,
      reputation: typeof tok.reputation === 'string' ? tok.reputation : undefined,
    })
  }
  return out
}

const BLOCKSCOUT_BASE = 'https://robinhoodchain.blockscout.com'
const DISCOVERY_TIMEOUT_MS = 6_000

/**
 * Fetches + parses a wallet's discovered ERC-20 holdings from Blockscout. Throws on network/HTTP
 * failure so the caller can fall back to the curated list (and flag "discovery temporarily limited").
 */
export async function fetchRobinhoodDiscovery(account: string): Promise<DiscoveredToken[]> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS)
  try {
    const res = await fetch(`${BLOCKSCOUT_BASE}/api/v2/addresses/${account}/token-balances`, {
      signal: controller.signal,
      headers: { accept: 'application/json' },
    })
    if (!res.ok) throw new Error(`blockscout discovery HTTP ${res.status}`)
    return parseBlockscoutHoldings(await res.json())
  } finally {
    clearTimeout(timer)
  }
}
